<template>
  <div>
    <HKLModal
      ref="addFoldersModalRef"
      class="add-folders"
      size="small"
      :title="$t('tenantProjects.greenFitOut.addFoldersTitle')"
      :closable="true"
      okBtnAction="add"
      :afterClose="resetData"
      :okBtnDisabled="!pendToAddFolders.length"
      @onOkClick="openAddReceiverModal"
    >
      <div class="green-fit-out-folders">
        {{ $t('tenantProjects.greenFitOut.greenFitOutFolders') }}
      </div>
      <template v-for="(section, sectionIndex) in publishedFolders">
        <HKLCollapse
          :key="sectionIndex"
          class="add-folders_section"
        >
          <div
            slot="title"
          >
            <label class="section-checkbox">
              <div>{{ `${section.displayOrder}. ${section.value}` }}</div>
              <input
                type="checkbox"
                :checked="getSectionStatus(section)"
                :disabled="isDisabledSection(section)"
                @change="(e) => onCheckAllChange(section, e.target.checked)"
              >
            </label>
          </div>
          <div
            slot="content"
            class=""
          >
            <template v-for="(folder, folderIndex) in section.sectionContent">
              <div
                :key="folder.value"
                class="line-checkout-outer"
              >
                <label
                  :key="folderIndex"
                  class="line-checkbox"
                >
                  <div>{{ `${folder.displayOrder}. ${folder.value}` }}</div>
                  <input
                    type="checkbox"
                    :checked="getFolderStatus(section, folder)"
                    :disabled="isDisabledFolder(section, folder)"
                    @change="(e) => onChange(section, folder, e.target.checked)"
                  >
                </label>
              </div>
            </template>
          </div>
        </HKLCollapse>
      </template>
    </HKLModal>
    <HKLModal
      ref="addReceiversModalRef"
      class="add-recievers"
      size="small"
      :title="$t('tenantProjects.greenFitOut.addReceiversTitleHint')"
      :closable="true"
    >
      <HKLForm ref="form">
        <EmailGroupEditor
          v-model="initialEmails"
        />
      </HKLForm>
      <div class="end-hint">
        {{ $t('tenantProjects.greenFitOut.addReceiversEndHint') }}
      </div>
      <template #footer>
        <HKLButton
          class="mg-right"
          color="darkblue"
          action="goback"
          @click="goBack"
        />
        <HKLButton
          color="orange"
          action="yesSendNow"
          @click="addFolders"
        />
      </template>
    </HKLModal>
  </div>
</template>

<script>
import { getGuidelines } from '@/api/settings/request'
import {
  createGreenFitoutSubmission,
  updateFoldersForGreenFitoutSubmission,
} from '@/api/tenantProjects/greenFitout.request'
import HKLCollapse from '@/controls/Collapse'
import HKLModal from '@/controls/Modal/HKLModal'
import { useRefModal } from '@/controls/Modal/modal.hook'
import { GUIDELINE_TYPE } from '@/modules/settings/engineers/consts'
import { GUIDELINE_STATUS } from '@/utils/locale'
import lodash from 'lodash'
import {
  onBeforeMount,
  ref,
  unref,
  computed,
} from 'vue-demi'
import { useProjectId } from '../postFitOutLetter/hook'
import {
  useCurrentFolderMap,
  useCurrentGreenFitout,
  useCurrentGreenFitoutStatus,
  useRefreshCurrentGreenFitout,
  useLatestAddedFolderHistory,
} from './hook'
import HKLForm from '@/controls/Form/HKLForm'
import { useRefForm } from '@/controls/Form/hook'
import HKLButton from '@/controls/Button/HKLButton'
import { getMainContractorEmail } from '@/api/postman/tenantProject'
import { useTenancyAccountsEmails } from '../hook'
import { emailSeparator } from '@/api/postman/consts'
import { utils } from '@/plugin/InputChange'
import EmailGroupEditor from '@/controls/Email/EmailGroupEditor.vue'
import { processDuplicateReceivers } from '@/api/postman/util'

export default {
  components: {
    HKLModal,
    HKLCollapse,
    HKLForm,
    EmailGroupEditor,
    HKLButton,
  },
  setup () {
    const projectId = useProjectId()
    const addFoldersModalRef = useRefModal()
    const currentGreenFitout = useCurrentGreenFitout()
    const { isStarted } = useCurrentGreenFitoutStatus()
    const publishedFolders = ref([])

    const sort = (sections) => {
      const orderedSection = lodash.orderBy(sections, 'order')
      orderedSection.map(section => {
        section.sectionContent = lodash.orderBy(section.sectionContent, 'order')
      })
      return orderedSection
    }
    onBeforeMount(async () => {
      const data = await getGuidelines(GUIDELINE_STATUS.PUBLISHED, GUIDELINE_TYPE.GREEN_FITOUT_FOLDER)
      const orderedSection = sort(data.guidelines)
      publishedFolders.value = orderedSection
    })

    const pendToAddFolders = ref([])
    const addedFolders = useCurrentFolderMap()
    const open = () => {
      if (!isStarted.value) {
        pendToAddFolders.value = lodash.cloneDeep(publishedFolders.value.filter(section => section.value.toLowerCase() !== 'others'))
      }
      addFoldersModalRef.open()
    }
    const resetData = () => {
      pendToAddFolders.value = []
      utils.resetDirty()
    }
    const getSecIndexInSections = (sections, section) => {
      let index = -1
      if (sections.length) {
        index = sections.findIndex((item) =>
          item.displayOrder === section.displayOrder &&
        item.value === section.value)
      }
      return index
    }
    const getFolderIndexInFolders = (section, folder) => {
      const index = section.sectionContent.findIndex((item) =>
        item.displayOrder === folder.displayOrder &&
        item.value === folder.value)
      return index
    }

    const isDisabledSection = (section) => {
      if (!isStarted.value) return false
      const addedIndex = getSecIndexInSections(addedFolders.value, section)
      if (addedIndex > -1) {
        const folderIndex = addedFolders.value[addedIndex].sectionContent.findIndex((folder) => getFolderIndexInFolders(section, folder) > -1)
        return folderIndex > -1
      }
      return false
    }
    const isDisabledFolder = (section, folder) => {
      if (!isStarted.value) return false
      const addedIndex = getSecIndexInSections(addedFolders.value, section)
      if (addedIndex > -1) {
        const folderIndex = getFolderIndexInFolders(addedFolders.value[addedIndex], folder)
        return folderIndex > -1
      }
      return false
    }
    const getSectionStatus = (section) => {
      const indexInPendTo = getSecIndexInSections(pendToAddFolders.value, section)
      const indexInAdded = getSecIndexInSections(addedFolders.value, section)
      return indexInPendTo > -1 || indexInAdded > -1
    }
    const getFolderStatus = (section, folder) => {
      const secIndexInPendTo = getSecIndexInSections(pendToAddFolders.value, section)
      let folderIndexInPendTo = -1
      let folderIndexInAdded = -1
      if (secIndexInPendTo > -1) {
        folderIndexInPendTo = getFolderIndexInFolders(pendToAddFolders.value[secIndexInPendTo], folder)
      }
      const secIndexInAdded = getSecIndexInSections(addedFolders.value, section)
      if (secIndexInAdded > -1) {
        folderIndexInAdded = getFolderIndexInFolders(addedFolders.value[secIndexInAdded], folder)
      }
      return folderIndexInPendTo > -1 || folderIndexInAdded > -1
    }

    const onChange = (section, folder, checked) => {
      const sectionIndex = getSecIndexInSections(pendToAddFolders.value, section)
      if (checked) {
        if (sectionIndex > -1) {
          pendToAddFolders.value[sectionIndex].sectionContent.push(folder)
        } else {
          const newSection = lodash.omit(section, 'sectionContent')
          newSection.sectionContent = [folder]
          pendToAddFolders.value.push(newSection)
        }
      } else {
        const folderIndex = getFolderIndexInFolders(pendToAddFolders.value[sectionIndex], folder)
        pendToAddFolders.value[sectionIndex].sectionContent.splice(folderIndex, 1)
        if (!pendToAddFolders.value[sectionIndex].sectionContent.length) {
          pendToAddFolders.value.splice(sectionIndex, 1)
        }
      }
    }
    const onCheckAllChange = (section, checked) => {
      if (checked) {
        pendToAddFolders.value.push(lodash.cloneDeep(section))
      } else {
        const toRemoveIndex = getSecIndexInSections(pendToAddFolders.value, section)
        pendToAddFolders.value.splice(toRemoveIndex, 1)
      }
    }

    const omitAttrs = (sections) => {
      return sections.map(section => {
        let { order, displayOrder, value, sectionContent } = section
        const newSectionContent = sectionContent.map(folder => {
          const { order, displayOrder, value } = folder
          return { order, displayOrder, value }
        })
        return { order,
          displayOrder,
          value,
          sectionContent: newSectionContent }
      })
    }
    const refresh = useRefreshCurrentGreenFitout()
    const pendToAddFoldersCopy = ref([])
    const forms = useRefForm()
    const addReceiversModalRef = useRefModal()
    const openAddReceiverModal = () => {
      pendToAddFoldersCopy.value = lodash.cloneDeep(pendToAddFolders.value)
      addReceiversModalRef.open()
    }

    const latestAddedFolderHistory = useLatestAddedFolderHistory(currentGreenFitout)
    const pendingReceivers = ref({})
    const initialEmails = computed({
      get () {
        let tmpReceivers = {}
        if (!isStarted.value) {
          const tenancyAccountsEmails = useTenancyAccountsEmails()
          const mainContractorEmail = getMainContractorEmail()
          const toEmails = new Set([...unref(tenancyAccountsEmails), mainContractorEmail])
          tmpReceivers = {
            to: Array.from(toEmails).join(emailSeparator),
            cc: '',
            bcc: '',
          }
        } else {
          tmpReceivers = {
            to: lodash.get(latestAddedFolderHistory.value, 'receivers.to', ''),
            cc: lodash.get(latestAddedFolderHistory.value, 'receivers.cc', ''),
            bcc: lodash.get(latestAddedFolderHistory.value, 'receivers.bcc', ''),
          }
        }
        return lodash.isEmpty(unref(pendingReceivers)) ? tmpReceivers : pendingReceivers.value
      },
      set (value) {
        pendingReceivers.value = value
      },
    })

    const addFolders = async () => {
      if (!forms.validate()) return
      const omitResult = omitAttrs(pendToAddFoldersCopy.value)
      const receivers = processDuplicateReceivers(initialEmails.value)
      if (!isStarted.value) {
        await createGreenFitoutSubmission(unref(projectId), { folders: omitResult, receivers })
      } else {
        await updateFoldersForGreenFitoutSubmission(unref(currentGreenFitout).id, { folders: omitResult, receivers })
      }
      addReceiversModalRef.close()
      resetData()
      await refresh()
    }
    const goBack = () => {
      addReceiversModalRef.close()
      addFoldersModalRef.open()
      pendToAddFolders.value = lodash.cloneDeep(pendToAddFoldersCopy.value)
    }

    return {
      isStarted,
      addFoldersModalRef: addFoldersModalRef.modal,
      addReceiversModalRef: addReceiversModalRef.modal,
      publishedFolders,
      pendToAddFolders,
      getSectionStatus,
      getFolderStatus,
      isDisabledSection,
      isDisabledFolder,
      open,
      onChange,
      onCheckAllChange,
      resetData,
      currentGreenFitout,
      openAddReceiverModal,
      pendingReceivers,
      initialEmails,
      addFolders,
      goBack,
      ...forms,
    }
  },
}
</script>
<style lang="scss" scoped>
.green-fit-out-folders {
  color: #0C3A58;
  font-weight: bold;
  font-size: 14px;
  border: solid 1px  #b5b5b5;
  border-bottom: none;
  padding: 10px 15px;
}
.add-folders_section {
  .line-checkbox, .section-checkbox {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .line-checkbox {
    font-weight: normal;
    div {
      width: 100%;
    }
  }
  .line-checkout-outer {
    padding-left: 15px;
    padding-right: 40px;
  }
  .line-checkout-outer:not(:last-child) {
    border-bottom: solid 1px  #b5b5b5;
  }
  ::v-deep .title-container {
    .title-content {
      margin-right: 12px;
      .section-checkbox > div {
        width: 100%;
      }
    }
    .arrow {
      height: 30px;
      line-height: 30px;
      font-size: 20px;
    }
  }
}
.add-recievers {
  .end-hint {
    margin-top: 20px;
    font-weight: bold;
  }
  .mg-right {
    margin-right: 10px;
  }
}
</style>
